import type { QueryFunctionContext, QueryKey } from '@tanstack/vue-query';
import createClient, { type Middleware } from 'openapi-fetch';
import { v4 as uuidv4 } from 'uuid';
import { store } from '@/app/store';
import { useUiStore } from '@/app/stores/ui';
import { analytics } from '@/services/Analytics';
import { idleWatcher } from '@/services/IdleWatcher';
import { clientEvents } from '@/services/telemetry/client-events';
import { sentryService } from '@/services/telemetry/sentry';
import { errorLogSummary, ParsedError } from './error-parser';
import {
  type BinaryInput,
  Http,
  httpStatusOf,
  type RequestOptions,
} from './http';
import type { paths } from './v1.gen';

type SchemaPath = keyof paths;

// Write endpoints safe to retry on transient network failures, keyed by schema
// path. GET/HEAD/OPTIONS retry by method in http.ts; PATCH/PUT/POST must be
// listed here. These are idempotent — re-sending produces the same result
// (upsert, overwrite, or read-only check). Excludes endpoints that create new
// resources or trigger irreversible state transitions (payment-intent create,
// confirm, capture). The key is the schema path, thus a path parameter of any
// form agrees, also a Stripe id that is not a UUID.
//
// POST /v1/sessions/{session_id}/gophoto-uploads is deliberately absent: it
// is NOT idempotent — every successful POST sends bytes to GoPhoto and creates
// a new photo on the user's PhotoPass. If a response is lost (network blip
// after upload), an HTTP-level retry would duplicate every photo. The
// frontend's per-uuid retry path in GoPhotoUpload.vue (AUTO_RETRY_DELAYS,
// retries only failed UUIDs reported by the backend) is the canonical
// retry layer. Until backend-side dedup lands (server-side idempotency on
// (session_id, image_id, gophoto_code)), the HTTP retry must stay off.
const RETRYABLE_ENDPOINTS_BY_METHOD: Record<string, ReadonlySet<SchemaPath>> = {
  POST: new Set<SchemaPath>([
    // The backend upserts the durable PrintJob row by idempotency_key and SAQ
    // dedups the enqueue on the same key.
    '/v1/print-jobs',
    // Both generation submits carry a client request_id that becomes the job
    // id, so a repeated POST returns the same job instead of a second
    // generation (and a second config claim).
    '/v1/generations',
    '/v1/group-portraits',
    '/v1/generations/config-reservations',
    '/v1/sessions/{session_id}/photos',
    '/v1/sessions/{session_id}/upscale-portraits',
    '/v1/sessions/{session_id}/analytics/errors',
    '/v1/payment-intents/cancel-reader',
    // The card paid already. The backend answers a repeated hold step of the
    // same checkout with the same hold.
    '/v1/payment-intents/{payment_intent_id}/hold',
  ]),
  PATCH: new Set<SchemaPath>(['/v1/sessions/{session_id}']),
  PUT: new Set<SchemaPath>([
    '/v1/kiosks/{kiosk_slug}/print-logo',
    '/v1/sessions/{session_id}/gophoto-code',
  ]),
};

// Per-request abort controllers keyed by requestId. Map preserves insertion
// order for FIFO cleanup. requestEndpoints maps requestId→schema path for
// selective abort by endpoint (e.g. abortGenerationRequests).
const abortControllers = new Map<string, AbortController>();
const requestEndpoints = new Map<string, string>();

// Monotonically increasing epoch, incremented on every abortAll(). Each
// request captures the epoch at creation; if it differs from the current
// epoch when the response arrives, it belongs to a previous session and is
// discarded. Closes the race where a TCP response is buffered in the kernel
// before abort fires, causing fetch to resolve successfully with stale data.
let epoch = 0;

// Extra init keys of an api.GET/POST/... call. openapi-fetch copies unknown
// init keys onto the Request it hands to transport().
interface TransportOptions {
  silent?: boolean;
  // Sends the request as a multipart form that http.ts builds (again on each
  // retry): the files plus the fields of `body`, or of `data` where the
  // schema has no body to type them (photo uploads name each file after its
  // photo uuid).
  multipart?: {
    data?: Record<string, unknown>;
    binaries: Record<string, BinaryInput>;
  };
}

interface CallMeta {
  signal: AbortSignal;
  silent?: boolean;
  headers: Record<string, string>;
}

interface RegisteredCall {
  endpoint: string;
  signal: AbortSignal;
  requestId: string;
  epoch: number;
  sessionId: string | null | undefined;
}

function createAbortController(endpoint: string): {
  signal: AbortSignal;
  requestId: string;
} {
  const controller = new AbortController();
  const requestId = uuidv4();
  abortControllers.set(requestId, controller);
  requestEndpoints.set(requestId, endpoint);

  // Immediate removal on abort (don't wait for request completion)
  controller.signal.addEventListener('abort', () => {
    abortControllers.delete(requestId);
    requestEndpoints.delete(requestId);
  });

  return { signal: controller.signal, requestId };
}

function cleanupRequest(requestId: string): void {
  abortControllers.delete(requestId);
  requestEndpoints.delete(requestId);
}

function apiTelemetryEndpoint(url: string): string {
  // Preserve operational query params such as kiosk api_key/key, ticket/QR
  // codes, session IDs, and image UUIDs in client events. These values are
  // internal join keys for Sentry/backend/Postgres correlation; Http redacts
  // only true credential params such as token/client_secret/password.
  return Http.sanitizeUrlForLogging(url) ?? url;
}

function stampErrorDuration(error: unknown, durationMs: number): void {
  const context = (error as ParsedError)?.context;
  if (!context || typeof context !== 'object') {
    return;
  }
  context.durationMs = durationMs;
}

// The body is parsed inside http.ts's timeout and error accounting, so a
// malformed or stalled body fails the request there; openapi-fetch reads the
// re-serialized copy.
async function bufferJsonResponse(response: Response): Promise<Response> {
  const json: unknown = await response.json();
  return new Response(JSON.stringify(json), {
    status: response.status,
    statusText: response.statusText,
    headers: { 'Content-Type': 'application/json' },
  });
}

function registerCall(endpoint: string): RegisteredCall {
  return {
    endpoint,
    ...createAbortController(endpoint),
    epoch,
    sessionId: store.sessionId,
  };
}

async function makeCancellableCall(
  url: string,
  call: RegisteredCall,
  method: string,
  data: unknown,
  binaries: Record<string, BinaryInput> | undefined,
  meta: CallMeta
): Promise<Response> {
  const idleWatcherPause = idleWatcher.pause();
  const { endpoint, signal, requestId, epoch: requestEpoch } = call;

  const requestParams: RequestOptions = {
    method,
    signal: composeSignals(signal, meta.signal),
    ...(meta.silent !== undefined && { silent: meta.silent }),
  };

  if (
    RETRYABLE_ENDPOINTS_BY_METHOD[method.toUpperCase()]?.has(
      endpoint as SchemaPath
    )
  ) {
    requestParams.retryable = true;
  }

  const diagnosticsContext = clientEvents.getContext();
  requestParams.headers = {
    'X-Kiosk-Trace-ID': clientEvents.getTraceId(),
    ...(diagnosticsContext.device_uuid && {
      'X-Kiosk-Device-ID': diagnosticsContext.device_uuid,
    }),
    ...(diagnosticsContext.build_version && {
      'X-Kiosk-Build-Version': diagnosticsContext.build_version,
    }),
    // Lets backend logs and the Sentry replay network panel tie the request
    // to the kiosk user session.
    ...(call.sessionId && { 'X-Session-ID': call.sessionId }),
    ...meta.headers,
  };

  const recordSuccess = !meta.silent;
  const requestStartedAt = performance.now();
  if (recordSuccess) {
    clientEvents.record(
      'api_request_started',
      {
        method,
        endpoint: apiTelemetryEndpoint(url),
        // Full request IDs are intentional telemetry join keys for frontend
        // client events and backend request correlation.
        request_id: requestId,
      },
      { area: 'api' }
    );
  }

  try {
    const response = await Http.makeRequest<Response>(
      url,
      requestParams,
      data,
      binaries,
      bufferJsonResponse
    );

    // Stale response detection: epoch may have been bumped by abortAll() while
    // this request was in flight (TCP response buffered before abort fires).
    if (requestEpoch !== epoch) {
      throw new ParsedError(
        `Stale response discarded: ${method} ${url} ` +
          `(request epoch ${requestEpoch}, current epoch ${epoch})`,
        { context: { isRequestManuallyAborted: true } }
      );
    }

    if (recordSuccess) {
      clientEvents.record(
        'api_request_succeeded',
        {
          method,
          endpoint: apiTelemetryEndpoint(url),
          request_id: requestId,
          duration_ms: Math.round(performance.now() - requestStartedAt),
        },
        { area: 'api' }
      );
    }

    return response;
  } catch (error) {
    stampErrorDuration(error, Math.round(performance.now() - requestStartedAt));
    const parsed = error as ParsedError;
    if (!(meta.silent || parsed?.context?.isRequestManuallyAborted)) {
      // The error holds the request body, which can hold the traits of the
      // people. The log holds the method, the path and the codes only.
      const summary = errorLogSummary(error);
      console.error(`[API] Error: ${method} ${endpoint}`, {
        status: summary.status,
        code: summary.code,
        name: summary.name,
      });
    }
    if (!parsed?.context?.isRequestManuallyAborted) {
      const eventType =
        (error as Error)?.name === 'TimeoutError'
          ? 'api_request_timeout'
          : 'api_request_failed';
      clientEvents.record(
        eventType,
        {
          method,
          endpoint: apiTelemetryEndpoint(url),
          request_id: requestId,
          duration_ms: Math.round(performance.now() - requestStartedAt),
          status_code: httpStatusOf(error),
          error_name: (error as Error)?.name,
          error_message: (error as Error)?.message,
        },
        {
          area: 'api',
          level: eventType === 'api_request_timeout' ? 'warning' : 'error',
        }
      );
    }
    throw error;
  } finally {
    cleanupRequest(requestId);
    idleWatcherPause.continue();
  }
}

// Compose two AbortSignals into one — aborts if either fires.
function composeSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  const controller = new AbortController();
  const forward = (src: AbortSignal) => {
    if (src.aborted) {
      controller.abort(src.reason);
    } else {
      src.addEventListener('abort', () => controller.abort(src.reason), {
        once: true,
      });
    }
  };
  forward(a);
  forward(b);
  return controller.signal;
}

const registeredCalls = new WeakMap<Request, RegisteredCall>();

// openapi-fetch runs the first onRequest synchronously inside api.GET/POST/...,
// before awaiting anything, so an abort issued right after the call already
// finds it registered, and it keeps the session it was made in.
const registerOnRequest: Middleware = {
  onRequest({ request, schemaPath }) {
    registeredCalls.set(request, registerCall(schemaPath));
  },
};

// http.ts sets Content-Type itself: JSON, or a multipart boundary that the
// application/json openapi-fetch stamps on every body would break.
function callerHeaders(request: Request): Record<string, string> {
  const headers: Record<string, string> = {};
  request.headers.forEach((value, name) => {
    if (name !== 'content-type') {
      headers[name] = value;
    }
  });
  return headers;
}

async function readJsonBody(request: Request): Promise<unknown> {
  if (request.method === 'GET' || request.method === 'HEAD') {
    return undefined;
  }
  const text = await request.text();
  return text ? JSON.parse(text) : undefined;
}

// http.ts owns the wire (auth and trace headers, timeout, retries, error
// parsing), so the body openapi-fetch serialized goes back to its JSON value
// and http.ts sends it again. Every non-2xx answer throws a ParsedError here.
async function transport(request: Request): Promise<Response> {
  const { silent, multipart } = request as Request & TransportOptions;
  const { pathname, search } = new URL(request.url);
  const call = registeredCalls.get(request) ?? registerCall(pathname);
  let body: unknown;
  try {
    body = await readJsonBody(request);
  } catch (error) {
    cleanupRequest(call.requestId);
    throw error;
  }
  return makeCancellableCall(
    `${pathname}${search}`,
    call,
    request.method,
    multipart?.data ?? body,
    multipart?.binaries,
    { signal: request.signal, silent, headers: callerHeaders(request) }
  );
}

// openapi-fetch builds a Request, which outside the browser needs an absolute
// URL. transport() sends only the path and query, to the page's own origin,
// so this origin is a placeholder that never reaches the network.
export const api = createClient<paths>({
  baseUrl: 'http://kiosk.invalid/api',
  fetch: transport,
});
api.use(registerOnRequest);

// transport() throws on every non-2xx answer, so only the data branch of an
// openapi-fetch result is ever reached.
export async function unwrap<T>(pending: Promise<{ data?: T }>): Promise<T> {
  return (await pending).data as T;
}

function abortByEndpoints(endpoints: SchemaPath[]): void {
  const toAbort: string[] = [];
  for (const [requestId, endpoint] of requestEndpoints) {
    if ((endpoints as string[]).includes(endpoint)) {
      toAbort.push(requestId);
    }
  }
  for (const requestId of toAbort) {
    const controller = abortControllers.get(requestId);
    if (controller && !controller.signal.aborted) controller.abort();
  }
}

// Increment epoch FIRST — this invalidates all in-flight requests so any
// response arriving after this point is discarded by the stale check above.
// Covers the TCP-already-buffered race.
function abortAll(): void {
  epoch++;
  const controllers = [...abortControllers.values()];
  // Clear maps before aborting: abort listeners will call delete() on an
  // already-empty Map (no-op). This avoids mutation-during-iteration fragility.
  abortControllers.clear();
  requestEndpoints.clear();
  for (const controller of controllers) controller.abort();
}

// Throws if store.sessionId is missing. Shows UI error + captures to Sentry.
// Used by every endpoint that requires a session context.
export function requireSessionId(): string {
  const sessionId = store.sessionId;
  if (sessionId) return sessionId;

  useUiStore().showError('api_error_missing_session_id');
  const error = new Error('api_error_missing_session_id') as Error & {
    context: Record<string, unknown>;
  };
  error.context = {
    kiosk_slug: store.kiosk?.slug,
    current_step: window.location.pathname,
    has_kiosk: Boolean(store.kiosk),
  };
  sentryService.captureException(error, {
    tags: { error_type: 'missing_session_id' },
    extra: error.context,
  });
  throw error;
}

interface CaptureErrorOptions {
  endpoint: string;
  extra?: Record<string, unknown>;
  skipOnStatus?: number[];
  // Backend error codes that are expected answers, not faults. A status
  // alone is too broad: the same endpoint can answer 404 for a lost session.
  skipOnCodes?: string[];
  // Fire-and-forget beacons (engagement/analytics) set this: a phone's flaky
  // network failing to deliver a telemetry POST is not actionable, and these
  // endpoints alone were the top event producers on the download portal.
  skipNetworkFailures?: boolean;
}

// backend/utils/errors.py NO_FACE_DETECTED_CODE — every backend path that can
// detect an empty frame reports this one code.
const NO_FACE_DETECTED_CODE = 'NO_FACE_DETECTED';

// The visitor stepped out of frame: Sentry needs neither the event nor the
// replay it would arm. Exported because the generation layer sees these
// errors again after this module has.
export function isEmptyFrameError(error: unknown): boolean {
  return (
    (error as { code?: unknown } | null | undefined)?.code ===
    NO_FACE_DETECTED_CODE
  );
}

// Exported so the analytics layer can make the same call: it wraps these
// endpoints in its own catch and would otherwise re-capture what
// skipNetworkFailures dropped here.
export function isBrowserNetworkFetchFailure(
  message: string,
  httpStatus?: number
): boolean {
  if (typeof httpStatus === 'number') {
    return false;
  }

  return /(?:^|:\s)(?:Load failed|Failed to fetch|NetworkError\b|net::ERR_[A-Z_]+)/.test(
    message
  );
}

// Captures API errors to Sentry, skipping intentional aborts. Network errors
// (after retries exhausted) and proxy 5xx (nginx/Caddy returning an HTML
// error page because the upstream backend was briefly down — deploy, granian
// worker hang, DB pool exhaustion) are captured at 'warning' level with stable
// fingerprints so they group by failure class instead of endpoint URL. Marks
// error with _sentryCaptured so downstream UI layer doesn't double-report.
export function captureApiError(
  error: unknown,
  options: CaptureErrorOptions
): void {
  const parsed = error as ParsedError & { _sentryCaptured?: boolean };
  if (parsed?.context?.isRequestManuallyAborted) return;
  if (isEmptyFrameError(parsed)) return;

  const httpStatus = httpStatusOf(error);
  if (httpStatus && options.skipOnStatus?.includes(httpStatus)) return;
  const errorCode = (parsed?.context?.response?.json as { code?: unknown })
    ?.code;
  if (
    typeof errorCode === 'string' &&
    options.skipOnCodes?.includes(errorCode)
  ) {
    return;
  }

  const msg = parsed?.message ?? '';
  // Mobile Safari (and Chrome on flaky cell connections) reports network
  // failures as "Load failed" / "Failed to fetch" / "NetworkError". These
  // are transient kiosk-on-mobile-data conditions, not engineering bugs.
  // The captureMessage path with a stable fingerprint collapses all
  // endpoints into one "Network unreachable" issue (was KIOSK-FRONTEND-GF
  // 1,268 users / EG 552 users / KX 428 users, each per-endpoint-URL).
  const isNetworkError = isBrowserNetworkFetchFailure(msg, httpStatus);
  if (isNetworkError && options.skipNetworkFailures) return;

  // Driver tags for the infra-5xx audit. Both are defensive: if the HTTP
  // layer never stamps the fields (i.e. before PR #156's makeRequest
  // changes land, or on a non-retryable endpoint), the tag silently
  // doesn't fire — no false positives.
  //
  //   connection_likely_restart: 502 with empty body returned in <100ms is
  //     the host-nginx "upstream unreachable" signature (container down,
  //     not a backend error). Lets us split deploy-window 5xx noise from
  //     persistent-outage alerts in Sentry rules.
  //   retries_attempted: how many retries fired before the final failure.
  //     >0 means the http.ts retry layer rode out a transient blip and
  //     still failed; ==0 (or absent) means the very first request blew
  //     up. Useful to gauge restart-window coverage.
  const ctx = parsed?.context as
    | (Record<string, unknown> & {
        response?: {
          status?: { code?: number };
          body?: { length?: number } | string | null;
          text?: string | null;
        };
        durationMs?: number;
        retriesAttempted?: number;
      })
    | undefined;
  const responseTextOrBody = ctx?.response?.text ?? ctx?.response?.body;
  const bodyLength =
    typeof responseTextOrBody === 'string'
      ? responseTextOrBody.length
      : (responseTextOrBody as { length?: number } | null | undefined)?.length;
  const isLikelyRestart502 =
    httpStatus === 502 &&
    bodyLength === 0 &&
    typeof ctx?.durationMs === 'number' &&
    ctx.durationMs < 100;
  const retryTag =
    typeof ctx?.retriesAttempted === 'number' && ctx.retriesAttempted > 0
      ? { retries_attempted: String(ctx.retriesAttempted) }
      : undefined;
  const restartTag = isLikelyRestart502
    ? { connection_likely_restart: 'true' }
    : undefined;

  // Proxy 5xx (host nginx / Caddy / CloudFlare upstream-down page). The
  // error-parser collapses the HTML body to "{code} {text}" so we detect
  // by status. Fingerprint by status only so KIOSK-FRONTEND-KE / D4 / NB /
  // M0 / P2 (~13k events across 5 per-endpoint groups) collapse to a
  // single, actionable "backend briefly unreachable" issue.
  const isProxy5xx =
    typeof httpStatus === 'number' && httpStatus >= 502 && httpStatus <= 504;

  if (isNetworkError) {
    sentryService.captureMessage('Network unreachable (browser fetch failed)', {
      tags: {
        api_endpoint: options.endpoint,
        error_class: 'network',
        ...retryTag,
        ...restartTag,
      },
      extra: { ...options.extra, original_message: msg.slice(0, 200) },
      level: 'warning',
      fingerprint: ['api-network-failed'],
    });
  } else if (isProxy5xx) {
    sentryService.captureMessage(
      `Backend infrastructure error (HTTP ${httpStatus})`,
      {
        tags: {
          api_endpoint: options.endpoint,
          error_class: 'infrastructure',
          http_status: String(httpStatus),
          ...retryTag,
          ...restartTag,
        },
        extra: options.extra,
        level: 'warning',
        fingerprint: ['api-infra-5xx', String(httpStatus)],
      }
    );
  } else {
    sentryService.captureException(error as Error, {
      tags: {
        api_endpoint: options.endpoint,
        ...retryTag,
        ...restartTag,
      },
      extra: options.extra,
      level: 'error',
    });
  }
  if (error && typeof error === 'object') {
    (error as { _sentryCaptured?: boolean })._sentryCaptured = true;
  }
}

// trackApiError — forwards the error code to backend analytics, adds a Sentry
// breadcrumb, then RE-THROWS. The re-throw lets callers .catch() to transform
// the error into a UI state.
export function trackApiError(
  error: unknown,
  fallbackErrorType?: string
): never {
  const parsed = error as ParsedError;
  const errorCode =
    (parsed?.context?.response?.json as { code?: string } | null)?.code ??
    fallbackErrorType ??
    'api_error_unknown';

  if (!parsed?.context?.isRequestManuallyAborted) {
    analytics.trackError(errorCode);
    sentryService.addBreadcrumb(
      'api',
      'API error tracked',
      { error_code: errorCode, endpoint: parsed?.context?.request?.url },
      'error'
    );
  }
  throw error;
}

type BreadcrumbCategory = 'api' | 'payment';

export interface BreadcrumbInput {
  message: string;
  data?: Record<string, unknown>;
}

export interface ApiQuery<T> {
  queryKey: QueryKey;
  queryFn: (context: QueryFunctionContext) => Promise<T>;
  staleTime?: number;
  structuralSharing?: (old: unknown, next: unknown) => unknown;
}

export interface InstrumentConfig<T> {
  span: string; // e.g. 'api.create_session'
  errorTag: string; // api_endpoint tag value
  category?: BreadcrumbCategory;
  tags?: Record<string, unknown>;
  before?: BreadcrumbInput;
  after?: (result: T) => BreadcrumbInput;
  errorExtra?: Record<string, unknown>;
  skipSentryOn?: number[]; // HTTP statuses to skip error capture on (e.g. [404])
  skipSentryOnCodes?: string[]; // Backend error codes to skip error capture on
  skipSentryCapture?: boolean; // Entirely skip captureApiError (for cancelPayment)
  trackErrorCode?: string; // If set, on error calls trackApiError instead of rethrow
}

// The breadcrumb before the call, the breadcrumb after the call and the Sentry
// extra of a failure have the same data. `more` adds the fields that the
// breadcrumb after the call reads from the result. Each one has its own copy,
// because the Sentry breadcrumb hook changes the data in place.
export function callBreadcrumbs<T>(
  before: string,
  after: string,
  data: Record<string, unknown>,
  more?: (result: T) => Record<string, unknown>
): Pick<InstrumentConfig<T>, 'before' | 'after' | 'errorExtra'> {
  return {
    before: { message: before, data: { ...data } },
    after: (result: T) => ({
      message: after,
      data: { ...data, ...more?.(result) },
    }),
    errorExtra: { ...data },
  };
}

export async function instrument<T>(
  config: InstrumentConfig<T>,
  fn: () => Promise<T>
): Promise<T> {
  const category = config.category ?? 'api';
  if (config.before) {
    sentryService.addBreadcrumb(
      category,
      config.before.message,
      config.before.data
    );
  }

  try {
    return await sentryService.withSpan(
      config.span,
      'http.client',
      async () => {
        const result = await fn();
        const after = config.after?.(result);
        if (after) {
          sentryService.addBreadcrumb(category, after.message, after.data);
        }
        return result;
      },
      config.tags ?? {}
    );
  } catch (error) {
    if (!config.skipSentryCapture) {
      captureApiError(error, {
        endpoint: config.errorTag,
        extra: config.errorExtra,
        skipOnStatus: config.skipSentryOn,
        skipOnCodes: config.skipSentryOnCodes,
      });
    }
    if (config.trackErrorCode !== undefined) {
      return trackApiError(error, config.trackErrorCode);
    }
    throw error;
  }
}

export const client = {
  abortAll,
  abortGeneration(): void {
    abortByEndpoints([
      '/v1/generations',
      '/v1/generations/{image_id}',
      '/v1/group-portraits',
    ]);
  },
  abortUploads(): void {
    // Also aborts GET list refreshes of the same path; acceptable — those are
    // background reads whose callers resolve to [].
    abortByEndpoints(['/v1/sessions/{session_id}/photos']);
  },
};
